/**
 * 통화 및 숫자 포맷팅 유틸리티 함수들
 */

/**
 * 금액을 천단위 콤마 숫자로 포맷팅. 화폐 단위(원/만원/천원 등)는 표기하지 않는다.
 * 폰트는 전 페이지 Pretendard 통일 — 숫자 정렬은 tabular-nums 가 담당.
 * @param amount - 포맷팅할 금액
 * @param options - locale 만 유효 (notation/currency 옵션은 후방호환용 무시)
 */
export const formatCurrency = (
  amount: number,
  options: {
    locale?: string;
    currency?: string;
    notation?: 'standard' | 'compact';
    compactDisplay?: 'short' | 'long';
  } = {}
): string => {
  const { locale = 'ko-KR' } = options;
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return amount.toLocaleString('ko-KR');
  }
};

/**
 * 간단한 금액 표시 — formatCurrency 와 동일 (단위 미표기).
 * 후방호환을 위해 별도 export 유지.
 */
export const formatCurrencySimple = (amount: number): string => {
  try {
    return new Intl.NumberFormat('ko-KR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return amount.toLocaleString('ko-KR');
  }
};

/**
 * 숫자를 한국어 로케일로 포맷팅
 * @param number - 포맷팅할 숫자
 * @param options - 포맷팅 옵션
 * @returns 포맷팅된 숫자 문자열
 */
export const formatNumber = (
  number: number,
  options: {
    locale?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {}
): string => {
  const {
    locale = 'ko-KR',
    minimumFractionDigits = 0,
    maximumFractionDigits = 2
  } = options;

  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(number);
  } catch (error) {
    return number.toLocaleString('ko-KR');
  }
};

/**
 * 퍼센트로 포맷팅
 * @param value - 퍼센트 값 (0-100)
 * @param options - 포맷팅 옵션
 * @returns 포맷팅된 퍼센트 문자열
 */
export const formatPercent = (
  value: number,
  options: {
    locale?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {}
): string => {
  const {
    locale = 'ko-KR',
    minimumFractionDigits = 1,
    maximumFractionDigits = 1
  } = options;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(value / 100);
  } catch (error) {
    return `${value.toFixed(1)}%`;
  }
};

/**
 * 날짜를 한국어 로케일로 포맷팅
 * @param date - 포맷팅할 날짜
 * @param options - 포맷팅 옵션
 * @returns 포맷팅된 날짜 문자열
 */
export const formatDate = (
  date: Date | string | number,
  options: {
    locale?: string;
    year?: 'numeric' | '2-digit';
    month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow';
    day?: 'numeric' | '2-digit';
    weekday?: 'long' | 'short' | 'narrow';
    hour?: 'numeric' | '2-digit';
    minute?: 'numeric' | '2-digit';
    second?: 'numeric' | '2-digit';
  } = {}
): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  
  const {
    locale = 'ko-KR',
    year = 'numeric',
    month = 'long',
    day = 'numeric',
    weekday,
    hour,
    minute,
    second
  } = options;

  try {
    return new Intl.DateTimeFormat(locale, {
      year,
      month,
      day,
      weekday,
      hour,
      minute,
      second,
    }).format(dateObj);
  } catch (error) {
    return dateObj.toLocaleDateString('ko-KR');
  }
};

/**
 * 상대적 시간 포맷팅 (예: "3일 전", "1시간 전")
 * @param date - 기준 날짜
 * @param baseDate - 비교할 기준 날짜 (기본값: 현재 시간)
 * @returns 상대적 시간 문자열
 */
export const formatRelativeTime = (
  date: Date | string | number,
  baseDate: Date | string | number = new Date()
): string => {
  const targetDate = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const base = typeof baseDate === 'string' || typeof baseDate === 'number' ? new Date(baseDate) : baseDate;
  
  const diffInMs = base.getTime() - targetDate.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInYears > 0) {
    return `${diffInYears}년 전`;
  } else if (diffInMonths > 0) {
    return `${diffInMonths}개월 전`;
  } else if (diffInDays > 0) {
    return `${diffInDays}일 전`;
  } else if (diffInHours > 0) {
    return `${diffInHours}시간 전`;
  } else if (diffInMinutes > 0) {
    return `${diffInMinutes}분 전`;
  } else if (diffInSeconds > 0) {
    return `${diffInSeconds}초 전`;
  } else {
    return '방금 전';
  }
};

/**
 * 파일 크기를 읽기 쉬운 형태로 포맷팅
 * @param bytes - 바이트 단위 크기
 * @returns 포맷팅된 파일 크기 문자열
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * 천 단위 콤마 추가
 * @param value - 포맷팅할 숫자 또는 문자열
 * @returns 콤마가 추가된 문자열
 */
export const formatNumberWithCommas = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(num)) return '0';
  return new Intl.NumberFormat('ko-KR').format(num);
};

/**
 * 콤마 제거하고 숫자로 변환
 * @param value - 포맷팅된 문자열
 * @returns 숫자
 */
export const parseNumberFromFormatted = (value: string): number => {
  const cleaned = value.replace(/,/g, '');
  return parseFloat(cleaned) || 0;
};
